import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QuratorLayout from '../components/qurator/QuratorLayout';
import QureLayout from '../components/qure/QureLayout';
import API from '../config/api';
import Swal from 'sweetalert2';
import './SupportTicket.css';

const SupportTicket = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Wait for auth to load before determining user type
  const isQure = user?.user_type?.toLowerCase() === 'qure';
  const Layout = isQure ? QureLayout : QuratorLayout;
  const apiPrefix = useMemo(() => isQure ? '/qure' : '/qurator', [isQure]);
  
  const [stats, setStats] = useState({ total_posts: 0, bug_reports: 0, questions: 0, feature_requests: 0 });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(searchParams.get('filter') || 'all');
  const [showModal, setShowModal] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    post_type: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    if (!apiPrefix) return;
    try {
      const response = await API.get(`${apiPrefix}/support/stats`);
      if (response.data && response.data.ok) {
        setStats(response.data.stats || { total_posts: 0, bug_reports: 0, questions: 0, feature_requests: 0 });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ total_posts: 0, bug_reports: 0, questions: 0, feature_requests: 0 });
    }
  }, [apiPrefix]);

  const fetchPosts = useCallback(async () => {
    if (!apiPrefix) return;
    try {
      setLoading(true);
      const response = await API.get(`${apiPrefix}/support/posts`, {
        params: { filter: filterType }
      });
      if (response.data && response.data.ok) {
        setPosts(response.data.posts || []);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
      if (!error.response || error.response.status !== 401) {
        Swal.fire('Error', 'Failed to load posts', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, apiPrefix]);

  useEffect(() => {
    if (!authLoading && user && apiPrefix) {
      fetchStats();
    }
  }, [authLoading, user, apiPrefix, fetchStats]);

  useEffect(() => {
    if (!authLoading && user && apiPrefix) {
      fetchPosts();
    }
  }, [filterType, apiPrefix, authLoading, user, fetchPosts]);

  const handleFilterChange = useCallback((filter) => {
    setFilterType(filter);
    setSearchParams({ filter });
  }, [setSearchParams]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (!formData.post_type) {
      Swal.fire('Warning', 'Please select a post type first!', 'warning');
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formDataUpload = new FormData();
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', `File ${file.name} is too large. Maximum size is 10MB.`, 'error');
        return;
      }
      formDataUpload.append('files', file);
    });
    formDataUpload.append('post_type', formData.post_type);

    try {
      const response = await API.post(`${apiPrefix}/support/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      if (response.data.ok) {
        setUploadedFiles(prev => [...prev, ...response.data.files]);
        Swal.fire('Success', 'Files uploaded successfully!', 'success');
      }
    } catch (error) {
      console.error('Upload error:', error);
      Swal.fire('Error', 'Failed to upload files', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const removeUploadedFile = (s3Key) => {
    setUploadedFiles(prev => prev.filter(f => f.s3Key !== s3Key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.post_type) {
      Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create new post
      const response = await API.post(`${apiPrefix}/support/posts`, {
        ...formData,
        uploaded_files: uploadedFiles
      });

      if (response.data.ok) {
        Swal.fire('Success', 'Post created successfully!', 'success');
        setShowModal(false);
        resetForm();
        fetchPosts();
        fetchStats();
      } else {
        Swal.fire('Error', response.data.error || 'Failed to create post', 'error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to save post', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', category: '', post_type: '' });
    setUploadedFiles([]);
    setUploadProgress(0);
  };

  const handleDeletePost = async (postId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await API.delete(`${apiPrefix}/support/posts/${postId}`);
        if (response.data.ok) {
          Swal.fire('Deleted!', 'Post has been deleted.', 'success');
          fetchPosts();
          fetchStats();
        }
      } catch (error) {
        console.error('Delete error:', error);
        Swal.fire('Error', 'Failed to delete post', 'error');
      }
    }
  };

  const toggleComments = useCallback(async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
    } else {
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
      
      // Fetch comments if not already loaded
      if (!comments[postId]) {
        try {
          const response = await API.get(`${apiPrefix}/support/posts/${postId}/comments`);
          if (response.data.ok) {
            setComments(prev => ({ ...prev, [postId]: response.data.comments }));
          }
        } catch (error) {
          console.error('Error fetching comments:', error);
        }
      }
    }
  }, [expandedComments, comments, apiPrefix]);

  const handleAddComment = async (postId) => {
    const commentText = newComment[postId];
    if (!commentText || !commentText.trim()) {
      Swal.fire('Error', 'Please enter a comment', 'error');
      return;
    }

    try {
      const response = await API.post(`${apiPrefix}/support/posts/${postId}/comments`, {
        comment: commentText
      });

      if (response.data.ok) {
        setNewComment(prev => ({ ...prev, [postId]: '' }));
        // Refresh comments
        const commentsResponse = await API.get(`${apiPrefix}/support/posts/${postId}/comments`);
        if (commentsResponse.data.ok) {
          setComments(prev => ({ ...prev, [postId]: commentsResponse.data.comments }));
        }
        fetchPosts(); // Refresh to update comment count
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      Swal.fire('Error', 'Failed to add comment', 'error');
    }
  };

  const openLightbox = (imageSrc) => {
    // Create a modal for image viewing
    Swal.fire({
      html: `<img src="${imageSrc}" style="max-width: 100%; max-height: 80vh; border-radius: 10px;" />`,
      showConfirmButton: false,
      background: 'rgba(0,0,0,0.9)',
      width: '90%',
      padding: 0
    });
  };

  const openVideo = (videoSrc) => {
    Swal.fire({
      html: `<video src="${videoSrc}" controls style="max-width: 100%; max-height: 80vh; border-radius: 10px;"></video>`,
      showConfirmButton: false,
      background: 'rgba(0,0,0,0.9)',
      width: '90%',
      padding: 0
    });
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const getBadgeClass = useCallback((postType) => {
    switch(postType) {
      case 'Bug Report': return 'badge-bug';
      case 'Question': return 'badge-question';
      case 'Feature Request': return 'badge-feature';
      default: return 'badge-help';
    }
  }, []);

  const getInitials = useCallback((firstName, lastName) => {
    return (firstName?.[0] || '') + (lastName?.[0] || '');
  }, []);

  // Show loading state while auth is loading
  if (authLoading) {
    const LoadingLayout = isQure ? QureLayout : QuratorLayout;
    return (
      <LoadingLayout>
        <div className="support-container">
          <div className="text-center py-5">
            <div className="qrt-loading" style={{ width: '50px', height: '50px', border: '4px solid rgba(255, 87, 34, 0.1)', borderTopColor: 'var(--support-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
            <p className="mt-3">Loading...</p>
          </div>
        </div>
      </LoadingLayout>
    );
  }

  // If no user after loading, show error
  if (!user) {
    const ErrorLayout = isQure ? QureLayout : QuratorLayout;
    return (
      <ErrorLayout>
        <div className="support-container">
          <div className="text-center py-5">
            <p>Please log in to access the support center.</p>
          </div>
        </div>
      </ErrorLayout>
    );
  }

  return (
    <Layout>
      <div className={`support-container ${isQure ? 'qure-theme' : 'qurator-theme'}`}>
        {/* Page Header */}
        <div className="page-header">
          <h1><i className="bi bi-headset me-3"></i>Support Center</h1>
          <p>Get help, report bugs, and suggest new features. Your feedback helps us improve!</p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-icon"><i className="bi bi-chat-dots"></i></div>
            <h2 className="stat-card-value">{stats.total_posts}</h2>
            <p className="stat-card-label">Total Posts</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><i className="bi bi-bug"></i></div>
            <h2 className="stat-card-value">{stats.bug_reports}</h2>
            <p className="stat-card-label">Bug Reports</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><i className="bi bi-question-circle"></i></div>
            <h2 className="stat-card-value">{stats.questions}</h2>
            <p className="stat-card-label">Questions</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><i className="bi bi-lightbulb"></i></div>
            <h2 className="stat-card-value">{stats.feature_requests}</h2>
            <p className="stat-card-label">Feature Requests</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            <i className="bi bi-grid me-1"></i>All Posts
          </button>
          <button 
            className={`filter-tab ${filterType === 'Bug Report' ? 'active' : ''}`}
            onClick={() => handleFilterChange('Bug Report')}
          >
            <i className="bi bi-bug me-1"></i>Bug Reports
          </button>
          <button 
            className={`filter-tab ${filterType === 'Question' ? 'active' : ''}`}
            onClick={() => handleFilterChange('Question')}
          >
            <i className="bi bi-question-circle me-1"></i>Questions
          </button>
          <button 
            className={`filter-tab ${filterType === 'Feature Request' ? 'active' : ''}`}
            onClick={() => handleFilterChange('Feature Request')}
          >
            <i className="bi bi-lightbulb me-1"></i>Feature Requests
          </button>
          <button 
            className={`filter-tab ${filterType === 'General Help' ? 'active' : ''}`}
            onClick={() => handleFilterChange('General Help')}
          >
            <i className="bi bi-info-circle me-1"></i>General Help
          </button>
        </div>

        {/* Posts Container */}
        {loading ? (
          <div className="text-center py-5">
            <div className="qrt-loading"></div>
            <p>Loading posts...</p>
          </div>
        ) : posts.length > 0 ? (
          <div className="posts-container">
            {posts.map(post => (
              <div key={post.id} className="post-card">
                <div className="post-header">
                  <div className="post-author">
                    <div className="author-avatar">
                      {getInitials(post.first_name, post.last_name)}
                    </div>
                    <div className="author-info">
                      <h5>{post.first_name} {post.last_name}</h5>
                      <span className="post-time">{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                  <div className="post-badges">
                    <span className={`badge-custom ${getBadgeClass(post.post_type)}`}>
                      {post.post_type}
                    </span>
                    <span className="badge-custom" style={{background: '#e7f3ff', color: '#0066cc'}}>
                      {post.category}
                    </span>
                  </div>
                </div>

                <h3 className="post-title">{post.title}</h3>
                <p className="post-description">{post.description}</p>

                {/* Attachments */}
                {post.attachments && post.attachments.length > 0 && (
                  <div className="post-attachments">
                    {post.attachments.map((attachment, idx) => {
                      const fileUrl = attachment.presigned_url || attachment.file_path;
                      const fileType = attachment.file_type || '';
                      
                      if (fileType.includes('image')) {
                        return (
                          <div 
                            key={idx} 
                            className="attachment-item" 
                            onClick={() => openLightbox(fileUrl)}
                          >
                            <img src={fileUrl} alt="Attachment" onError={(e) => {
                              e.target.src = attachment.file_path || ''; // Fallback to original path
                            }} />
                          </div>
                        );
                      } else if (fileType.includes('video')) {
                        return (
                          <div 
                            key={idx} 
                            className="attachment-item" 
                            onClick={() => openVideo(fileUrl)}
                          >
                            <video src={fileUrl}></video>
                            <div className="video-overlay"><i className="bi bi-play-fill"></i></div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}

                {/* Post Actions */}
                <div className="post-actions">
                  <button 
                    className="action-btn" 
                    onClick={() => toggleComments(post.id)}
                    type="button"
                  >
                    <i className="bi bi-chat"></i>
                    <span>Comments ({post.comment_count || 0})</span>
                  </button>
                  <button 
                    className="action-btn delete-btn" 
                    onClick={() => handleDeletePost(post.id)}
                    type="button"
                  >
                    <i className="bi bi-trash"></i>
                    <span>Delete</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedComments[post.id] && (
                  <div className="comments-section">
                    {comments[post.id] && comments[post.id].length > 0 ? (
                      comments[post.id].map((comment, idx) => (
                        <div key={idx} className="comment-item">
                          <div className="comment-header">
                            <div className="comment-avatar">
                              {getInitials(comment.first_name, comment.last_name)}
                            </div>
                            <div className="comment-author">
                              {comment.first_name} {comment.last_name}
                            </div>
                            <div className="comment-time">
                              {formatDate(comment.created_at)}
                            </div>
                          </div>
                          <div className="comment-text">{comment.comment}</div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted" style={{ textAlign: 'left', margin: 0 }}>No comments yet</p>
                    )}

                    {/* Comment Form */}
                    <div className="comment-form">
                      <textarea
                        className="comment-input"
                        placeholder="Write a helpful comment..."
                        value={newComment[post.id] || ''}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                        rows="3"
                      />
                      <button 
                        className="btn btn-primary-custom"
                        onClick={() => handleAddComment(post.id)}
                      >
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <i className="bi bi-chat-dots"></i>
            <h5>No posts found</h5>
            <p>You haven't created any support posts yet. Click the + button to create your first post!</p>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <button 
        className="floating-action-btn" 
        onClick={() => setShowModal(true)}
        title="Create Post"
      >
        <i className="bi bi-plus"></i>
      </button>

      {/* Create Post Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-plus-circle me-2"></i>
                Create New Support Post
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group col-md-8">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control-modern"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      required
                      maxLength={200}
                      placeholder="Describe your issue briefly"
                    />
                  </div>
                  <div className="form-group col-md-4">
                    <label className="form-label">Type *</label>
                    <select
                      className="form-control-modern"
                      value={formData.post_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, post_type: e.target.value }))}
                      required
                    >
                      <option value="">Select type</option>
                      <option value="Bug Report">Bug Report</option>
                      <option value="Question">Question</option>
                      <option value="Feature Request">Feature Request</option>
                      <option value="General Help">General Help</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    className="form-control-modern"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    required
                  >
                    <option value="">Select category</option>
                    <option value="VR Simulations">VR Simulations</option>
                    <option value="MR Simulations">MR Simulations</option>
                    <option value="User Interface">User Interface</option>
                    <option value="Performance">Performance</option>
                    <option value="Authentication">Authentication</option>
                    <option value="File Management">File Management</option>
                    <option value="Mobile App">Mobile App</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <textarea
                    className="form-control-modern"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows="5"
                    required
                    placeholder="Provide detailed information about your issue. Include steps to reproduce if it's a bug."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Attachments (Optional)</label>
                  <div 
                    className="file-upload-area" 
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="upload-icon">
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                    <h6>Click to upload screenshots or videos</h6>
                    <p className="text-muted mb-0">Images and videos up to 10MB each</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  
                  {isUploading && (
                    <div className="upload-progress">
                      <div className="progress">
                        <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                      <p className="text-center mt-2 mb-0">Uploading... {uploadProgress}%</p>
                    </div>
                  )}
                  
                  {uploadedFiles.length > 0 && (
                    <div className="uploaded-files-list">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="uploaded-file-item">
                          <span>
                            <i className="bi bi-check-circle text-success me-2"></i>
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeUploadedFile(file.s3Key)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-custom"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary-custom"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-1"></i>
                        Create Post
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SupportTicket;
